import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

interface SwipeToConfirmProps {
  /** 中央に表示するヒント文言（例: 「右にスワイプして送信」） */
  label: string;
  /** スライダー右端まで到達したときに発火するコールバック（一度だけ） */
  onConfirm: () => void;
  /** スライダーが 80% に達したかどうか（active 状態）を親に伝えるためのコールバック */
  onActiveChange?: (active: boolean) => void;
  /**
   * 「OK 状態（最後までスワイプして指を離した状態）」の変化を双方向で親に伝える。
   * 95% を超えてリリース時に true、OK 状態でつまみをクリックして戻した時に
   * false が渡る。
   */
  onConfirmedChange?: (confirmed: boolean) => void;
  /**
   * 操作ロック（送信中など）。操作は受け付けないが、確定済み（OK）の
   * 見た目と状態は保持する（送信中に OFF へ戻って見えないようにする）。
   * 未確定のままロックされた場合のみつまみを左端へ戻す。
   */
  disabled?: boolean;
  /**
   * OFF 状態のつまみ中央アイコン（material-symbols 名）。
   * 確定時は自動で `check` に切り替わる。既定は `chevron_right`。
   */
  iconName?: string;
  /** カラー指定。既定はテーマブルー。 */
  accentColor?: string;
  /** 確定後の表示文言（例: 「送信中...」）。 */
  confirmedLabel?: string;
}

// ===== スタイル =====

const SLIDER_HEIGHT = 40;
const THUMB_SIZE = 40;

const Track = styled.div<{ $disabled: boolean; $isConfirmed: boolean }>`
  position: relative;
  width: 100%;
  height: ${SLIDER_HEIGHT}px;
  border-radius: ${SLIDER_HEIGHT / 2}px;
  background: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) {
      return "var(--soremo-blue)";
    }
    if ($disabled) {
      return "linear-gradient(135deg, rgba(40, 44, 52, 0.22), rgba(40, 44, 52, 0.10))";
    }
    return "linear-gradient(135deg, rgba(20, 24, 32, 0.30), rgba(20, 24, 32, 0.14))";
  }};
  backdrop-filter: ${({ $isConfirmed }) =>
    $isConfirmed ? "none" : "blur(16px) saturate(160%)"};
  -webkit-backdrop-filter: ${({ $isConfirmed }) =>
    $isConfirmed ? "none" : "blur(16px) saturate(160%)"};
  border: 1px solid
    ${({ $disabled, $isConfirmed }) => {
      if ($isConfirmed) {
        return "var(--soremo-blue)";
      }
      return $disabled ? "rgba(255, 255, 255, 0.14)" : "rgba(255, 255, 255, 0.24)";
    }};
  box-shadow: ${({ $isConfirmed }) =>
    $isConfirmed
      ? "0 0 0 1px rgba(255, 255, 255, 0.25) inset, 2px 4px 8px 3px rgba(0, 154, 206, 0.35)"
      : "inset 0 1px 0 rgba(255, 255, 255, 0.22), inset 0 -1px 0 rgba(0, 0, 0, 0.16)"};
  cursor: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) return $disabled ? "default" : "pointer";
    if ($disabled) return "not-allowed";
    return "default";
  }};
  user-select: none;
  touch-action: none;
  overflow: visible;
  transition: background 0.25s ease-out, border-color 0.25s ease-out,
    box-shadow 0.25s ease-out;
`;

const Fill = styled.div<{
  $width: number;
  $disabled: boolean;
  $isConfirmed: boolean;
}>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $width }) => $width}px;
  border-radius: ${THUMB_SIZE / 2}px;
  background: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) {
      return "var(--soremo-blue)";
    }
    if ($disabled) {
      return "rgba(255, 255, 255, 0.18)";
    }
    return "rgba(255, 255, 255, 0.92)";
  }};
  transition: width 0.05s linear, background 0.25s ease-out;
  pointer-events: none;
`;

const Hint = styled.div<{ $isActive: boolean; $isConfirmed: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ $isActive, $isConfirmed }) => {
    if ($isConfirmed) {
      return "rgba(255, 255, 255, 1)";
    }
    return $isActive ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.7)";
  }};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-shadow: ${({ $isConfirmed }) =>
    $isConfirmed
      ? "0 1px 2px rgba(0, 0, 0, 0.2)"
      : "0 0 8px rgba(0, 0, 0, 0.35)"};
  transition: color 0.25s ease-out, text-shadow 0.25s ease-out;
`;

const Thumb = styled.div<{
  $left: number;
  $accent: string;
  $disabled: boolean;
  $isActive: boolean;
  $isConfirmed: boolean;
}>`
  position: absolute;
  top: 0;
  left: ${({ $left }) => $left}px;
  width: ${THUMB_SIZE}px;
  height: ${THUMB_SIZE}px;
  border-radius: 50%;
  background: ${({ $disabled, $isConfirmed, $accent }) => {
    /* 確定状態はロック（送信中）でも見た目を維持する（$disabled より優先） */
    if ($isConfirmed) {
      return "rgba(255, 255, 255, 0.95)";
    }
    if ($disabled) {
      return "rgba(167, 168, 169, 0.6)";
    }
    return $accent;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) return $disabled ? "default" : "pointer";
    if ($disabled) return "not-allowed";
    return "grab";
  }};
  box-shadow: ${({ $disabled, $isActive, $isConfirmed }) => {
    if ($isConfirmed) {
      return `
        0 0 0 1px rgba(255, 255, 255, 0.9),
        0 0 16px rgba(255, 255, 255, 0.8),
        0 2px 6px rgba(0, 0, 0, 0.15)
      `;
    }
    if ($disabled) {
      return "0 2px 6px rgba(0, 0, 0, 0.2)";
    }
    if ($isActive) {
      return `
        0 0 0 1px rgba(255, 255, 255, 0.8),
        0 0 14px rgba(255, 255, 255, 0.85),
        0 2px 10px 4px rgba(0, 154, 206, 0.6),
        0 0 28px 8px rgba(0, 154, 206, 0.5)
      `;
    }
    return `
      0 0 0 1px rgba(255, 255, 255, 0.6),
      0 0 12px rgba(255, 255, 255, 0.7),
      0 2px 8px 3px rgba(0, 154, 206, 0.4),
      0 0 20px 6px rgba(0, 154, 206, 0.35)
    `;
  }};
  transition: left 0.05s linear, box-shadow 0.25s ease-out, background 0.25s
    ease-out;

  &:active {
    cursor: ${({ $disabled, $isConfirmed }) => {
      if ($isConfirmed) return $disabled ? "default" : "pointer";
      if ($disabled) return "not-allowed";
      return "grabbing";
    }};
  }

  .material-symbols-rounded {
    color: ${({ $isConfirmed }) =>
      $isConfirmed ? "var(--soremo-blue)" : "white"};
    font-size: 24px;
    filter: ${({ $isConfirmed }) =>
      $isConfirmed
        ? "none"
        : "drop-shadow(0 0 4px rgba(255, 255, 255, 0.4))"};
    transition: color 0.25s ease-out;
  }
`;

// ===== 本体 =====

const SwipeToConfirm: React.FC<SwipeToConfirmProps> = ({
  label,
  onConfirm,
  onActiveChange,
  onConfirmedChange,
  disabled = false,
  iconName = "chevron_right",
  accentColor = "var(--soremo-blue)",
  confirmedLabel,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const maxLeft = Math.max(0, trackWidth - THUMB_SIZE);
  // 80% 到達で active（つまみのグローを強める）、95% 到達して指を離した瞬間に確定発火
  const activeThreshold = maxLeft * 0.8;
  const confirmThreshold = maxLeft * 0.95;
  const isActive = thumbLeft >= activeThreshold && !disabled;

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  useEffect(() => {
    onConfirmedChange?.(isConfirmed);
  }, [isConfirmed, onConfirmedChange]);

  // トラック幅を計測
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const sync = () => setTrackWidth(el.getBoundingClientRect().width);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** つまみを左端に戻し、確定状態も解除する */
  const reset = useCallback(() => {
    setIsConfirmed(false);
    setThumbLeft(0);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      // OK 状態でつまみをタップ／クリックされたら承認前の状態に戻す
      if (isConfirmed) {
        reset();
        return;
      }
      draggingRef.current = true;
      startXRef.current = e.clientX - thumbLeft;
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [disabled, isConfirmed, thumbLeft, reset],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current || disabled || isConfirmed) return;
      const next = Math.min(
        maxLeft,
        Math.max(0, e.clientX - startXRef.current),
      );
      setThumbLeft(next);
    },
    [disabled, isConfirmed, maxLeft],
  );

  const handlePointerUp = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (thumbLeft >= confirmThreshold && !disabled && !isConfirmed) {
      setThumbLeft(maxLeft);
      setIsConfirmed(true);
      onConfirm();
    } else {
      reset();
    }
  }, [thumbLeft, confirmThreshold, disabled, isConfirmed, maxLeft, onConfirm, reset]);

  // 未確定のままロックされたときだけリセットする。確定済み（OK）は
  // 送信中ロックでも見た目・状態を保持し、OFF へ戻る混乱を避ける
  // （送信失敗後も確定のままなので、再スワイプなしでリトライできる）。
  useEffect(() => {
    if (disabled && !isConfirmed) {
      reset();
    }
  }, [disabled, isConfirmed, reset]);

  // OK 状態でトラック余白部分をクリックされた場合のリセット（つまみクリックは
  // Thumb 側 onPointerDown が拾うので、ここでは currentTarget 一致のときだけ反応する）
  const handleTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || !isConfirmed) return;
      if (e.target !== e.currentTarget) return;
      reset();
    },
    [disabled, isConfirmed, reset],
  );

  const fillWidth = thumbLeft + THUMB_SIZE;
  const displayLabel = isConfirmed && confirmedLabel ? confirmedLabel : label;

  return (
    <Track
      ref={trackRef}
      $disabled={disabled}
      $isConfirmed={isConfirmed}
      onPointerDown={handleTrackPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <Fill
        $width={fillWidth}
        $disabled={disabled}
        $isConfirmed={isConfirmed}
      />
      <Hint $isActive={isActive} $isConfirmed={isConfirmed}>
        {displayLabel}
      </Hint>
      <Thumb
        $left={thumbLeft}
        $accent={accentColor}
        $disabled={disabled}
        $isActive={isActive}
        $isConfirmed={isConfirmed}
        onPointerDown={handlePointerDown}
      >
        <span className="material-symbols-rounded">
          {isConfirmed ? "check" : iconName}
        </span>
      </Thumb>
    </Track>
  );
};

export default SwipeToConfirm;
